import { TokenProvider } from "@faro-lotv/service-wires";
import {
  ActivityApiConstructorParams,
  ActivityApiPaginatedResponse,
  FetchCreditTransactionProps,
  SendLsDataV2DebugInfoProps,
  PublishActivityRequest,
} from "@api/activity-api/activity-api-types";
import { isActivityApiPaginatedResponse } from "@api/activity-api/activity-api-type-guards";
import { assert } from "@faro-lotv/foundation";
import { CompanyId, ProjectId } from "@custom-types/base-types";

/** Number of activity items per page that we will fetch from the ActivityAPI */
const ITEMS_PER_PAGE = 30;
/**
 * An api client to handle request to activity tracking api
 * https://faro01.atlassian.net/wiki/spaces/HB/pages/3873013913/Activity+Tracking+API+aka+Analytics+Backend
 *
 * OpenApi documentation:
 * https://activity.api.dev.holobuilder.eu/swagger/index.html#tag/Activity/paths/~1v1~1activities/post
 */
export class ActivityApiClient {
  #companyId?: CompanyId;
  #projectId?: ProjectId;
  #tokenProvider: TokenProvider;
  #activityApiBaseUrl: string;
  #clientId?: string;

  /** Create a new activity api client */
  constructor(params: ActivityApiConstructorParams) {
    this.#companyId = params.companyId;
    this.#projectId = params.projectId;
    this.#activityApiBaseUrl = params.activityApiBaseUrl;
    this.#tokenProvider = params.tokenProvider;
    this.#clientId = params.clientId;
  }

  /**
   * @returns Paginated list of credit transactions
   */
  async fetchCreditTransaction({
    companyId,
    projectId,
    after,
    pageSize = ITEMS_PER_PAGE,
  }: FetchCreditTransactionProps = {}): Promise<ActivityApiPaginatedResponse> {
    const searchParams = new URLSearchParams();
    searchParams.append("tags", "tokens");

    // Adding companyId if already provided in constructor
    const companyIdToSearch = this.#companyId ?? companyId;
    if (companyIdToSearch) {
      searchParams.append("companyId", companyIdToSearch.toString());
    }

    // Adding projectId if already provided in constructor
    const projectIdToSearch = this.#projectId ?? projectId;
    if (projectIdToSearch) {
      searchParams.append("projectId", projectIdToSearch.toString());
    }

    // Adding the after cursor if provided to fetch the next page
    if (after) {
      searchParams.append("after", after);
    }
    searchParams.append("pageSize", pageSize.toString());

    const url = new URL(
      `${this.#activityApiBaseUrl}/v1/activities?${searchParams.toString()}`
    );

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${await this.#tokenProvider()}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ...(this.#clientId && { "X-Holobuilder-Component": this.#clientId }),
      },
    });
    if (!res.ok) {
      throw new Error(
        `Transactions request failed: ${res.status} ${res.statusText}`
      );
    }

    const response = await res.json();

    if (!isActivityApiPaginatedResponse(response)) {
      throw new Error("ActivityAPI returned invalid response", {
        cause: response,
      });
    }

    return response;
  }

  /**
   * Uploads the LsDataV2 Zip File to the activity tracking api for debugging purposes.
   * @param companyId The company id
   * @param projectId The project id
   * @param revisionId The revision id
   * @param url The url of the uploaded lsdataV2 zip file
   * @param type The type of the lsdataV2 zip file
   */
  async sendLsDataV2DebugInfo({
    companyId,
    projectId,
    revisionId,
    url,
    elementType,
    userId,
  }: SendLsDataV2DebugInfoProps): Promise<void> {
    companyId = (this.#companyId ?? companyId)?.toString();
    projectId = (this.#projectId ?? projectId)?.toString();
    assert(typeof companyId === "string", "companyId is required");
    assert(typeof projectId === "string", "projectId is required");

    const source = {
      type: this.#clientId?.split("/")[0] ?? "sphere-dashboard",
      version: this.#clientId?.split("/")[1] ?? "0.0.0",
    };

    const body: PublishActivityRequest = {
      timestamp: new Date().toISOString(),
      tags: ["LsDataV2", elementType],
      source,
      context: {
        companyId,
        projectId,
        userId,
        workflowId: revisionId,
        correlationId: revisionId,
      },
      action: {
        category: "Debugging",
        type: "DebugDataDump",
        data: {
          url,
          revisionId,
          elementType,
        },
      },
    };

    // The query parameter projectId is required for token validation reasons
    const route = new URL(
      `${this.#activityApiBaseUrl}/v1/activities?projectId=${projectId}`
    );
    const res = await fetch(route.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${await this.#tokenProvider()}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "Content-Type": "application/json",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ...(this.#clientId && { "X-Holobuilder-Component": this.#clientId }),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "Failed to parse response");
      throw new Error(`ActivityAPI request failed: ${res.status}: ${body}`);
    }
  }
}
